import { Home, AddBusiness, AttachMoney } from '@mui/icons-material';
import {
  Grid,
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { Card } from '../../../../components';
import { FC, useContext } from 'react';
import { formatDate, formatMoney } from '../../../../helpers';
import { ISpec, ISpecOptionsDetail } from '../../../../models';
import { Item } from '../OptionItem';
import { FinancingInformation } from '../financing-information';
import { UserContext } from '../../../../context';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { GridOption } from '../price-calculation-grid';

interface IPrintSelectionDetailInfo {
  spec: ISpec;
  specOptions: ISpecOptionsDetail;
  houseSaleContingencyLocation: string;

  hasHouseSaleContingency: boolean;
  values: any;
}

export const PrintSelectionDetailsInfo: FC<IPrintSelectionDetailInfo> = ({
  spec,
  specOptions,
  houseSaleContingencyLocation,
  hasHouseSaleContingency,
  values
}) => {
  const { user } = useContext(UserContext);

  const isAdminOrSalesAdmin =
    user?.userRoles?.includes('BL_Admin') || user?.userRoles?.includes('BL_SalesAdmin');

  const classes = useStyles();

  return (
    <Box
      sx={{
        '@media print': {
          '*': { transition: 'none !important' }
        }
      }}
    >
      <Card cardContentRootClassName={classes.cardContent} icon={<Home />} title="Details">
        <Grid
          container
          sx={theme => ({
            padding: theme.spacing(0.25)
          })}
        >
          <Grid item xs={12} sm={6}>
            <Item label="Created On: " value={formatDate(specOptions?.createdOn)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Item label="Status: " value={specOptions?.status} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Item label="Purchaser: " value={specOptions?.client} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Item label="Neighborhood: " value={spec?.neighborhoodDescription} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Item label="Lot: " value={spec?.lotNumber} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Item label="Plan: " value={spec?.planDescription} />
          </Grid>
        </Grid>
      </Card>
      <Card
        cardContentRootClassName={classes.cardContent}
        icon={<AddBusiness />}
        title="House Sale Contingency Information"
      >
        <Grid
          container
          sx={theme => ({
            padding: theme.spacing(0.25)
          })}
        >
          <Grid display={'none'} displayPrint={'block'} item xs={6}>
            <div>Contingent House Location (If Applicable):</div>
            {houseSaleContingencyLocation ?? ''}
          </Grid>
        </Grid>
      </Card>
      {isAdminOrSalesAdmin && spec?.isContractComplete && (
        <FinancingInformation
          cardContentRootClassName={classes.cardContent}
          buildId={spec?.buildId}
          neighborhoodId={spec?.neighborhoodId}
          financingType={values?.financingType}
          lenderId={values?.lenderId}
        />
      )}
      <Card
        cardContentRootClassName={classes.cardContent}
        icon={<AttachMoney />}
        title="Price Calculations"
      >
        <Grid
          container
          sx={theme => ({
            padding: theme.spacing(0.25)
          })}
        >
          <Grid item xs={9}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center', border: 'none' }}></TableCell>
                    <TableCell
                      sx={{ textAlign: 'center', padding: 0, border: 'none', fontWeight: 600 }}
                    >
                      Original
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: 'center', padding: 0, border: 'none', fontWeight: 600 }}
                    >
                      Adjustments/Incentives
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: 'center', padding: 0, border: 'none', fontWeight: 600 }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <GridOption.PrintRow
                    label="Base Price:"
                    originalPrice={formatMoney(specOptions?.basePrice_Original)}
                    incentive={formatMoney(specOptions?.basePrice_Adjustment)}
                    finalPrice={formatMoney(specOptions?.basePrice)}
                  />
                  <GridOption.PrintRow
                    label="Lot Premium:"
                    originalPrice={formatMoney(specOptions?.lotPremium_Original)}
                    incentive={formatMoney(specOptions?.lotPremium_Adjustment)}
                    finalPrice={formatMoney(specOptions?.lotPremium)}
                  />
                  <GridOption.PrintRow
                    label="Options at Contract:"
                    finalPrice={formatMoney(specOptions?.contractUpgradeAmount)}
                  />
                  <GridOption.PrintRow
                    label="Ten Day Options:"
                    finalPrice={formatMoney(specOptions?.tenDayOptionsPrice)}
                  />
                  <GridOption.PrintRow
                    label="Incentives:"
                    finalPrice={formatMoney(specOptions?.incentives)}
                  />
                  <GridOption.PrintRow
                    label="Total Price:"
                    finalPrice={formatMoney(specOptions?.totalPriceAdjusted)}
                  />
                  <GridOption.PrintRow
                    label="Option Deposit Cap:"
                    originalPrice={''}
                    finalPrice={formatMoney(specOptions?.appraisalAlarmValue)}
                  />
                  <GridOption.PrintRow
                    label="Option Deposit Cap Overage:*"
                    color={
                      specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                        ? 'red'
                        : undefined
                    }
                    finalPrice={formatMoney(specOptions?.upgradeCapOverage)}
                  />
                </TableBody>
              </Table>
            </TableContainer>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                sx={{
                  color:
                    specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                      ? 'red'
                      : undefined
                }}
              >
                *Deposit amount due based on selected option.
              </Typography>
              <br />
              <Typography
                variant="caption"
                sx={{
                  color:
                    specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                      ? 'red'
                      : undefined
                }}
              >
                Any additional options will be added to the amount due at final contract acceptance.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  calculationText: {
    '@media (min-width: 960px)': {
      whiteSpace: 'nowrap'
    }
  },
  cardContent: {
    padding: theme.spacing(0.25),
    paddingBottom: `${theme.spacing(0.5)} !important`,
    marginTop: `${theme.spacing(0.25)} !important`
  }
}));
